<template>
  <div
    class="inventory-box"
    :class="{ readonly: readonly }"
  >
    <div class="inventory-header">
      <span class="inventory-title">{{ title || 'Equipment & Inventory' }}</span>
      <button
        v-if="showClose"
        class="inventory-close"
        aria-label="Close inventory"
        @click="$emit('close')"
      >
        ×
      </button>
    </div>

    <!-- Entity Name and Portrait -->
    <div class="entity-preview-section">
      <div class="entity-name-header">
        {{ entityDisplayName }}
      </div>
      <div class="entity-portrait-container">
        <EntityRepresentation
          v-if="portraitConfig"
          :services="services"
          :world="portraitConfig.world"
          :entity-id="portraitConfig.entityId"
          :size="120"
          :zoom="3"
          @tick="handlePortraitTick"
        />
      </div>
    </div>

    <!-- Equipment Panel (Paper Doll Layout) -->
    <div class="equipment-panel">
      <!-- Row 1: Head -->
      <div class="equipment-row">
        <div
          class="equipment-spacer"
          :style="{ width: slotSize + 'px', height: slotSize + 'px' }"
        />
        <EquipmentSlotComponent
          slot-name="head"
          label="Head"
          :slot-info="getEquipmentSlotInfo('head')"
          :slot-size="slotSize"
          :is-valid-target="isValidDropTarget('head')"
          :is-dragging="isDragging"
          :drag-over-slot="dragOverEquipSlot"
          :readonly="readonly"
          @dragstart="onEquipDragStart($event, 'head')"
          @dragend="onDragEnd"
          @dragover="onEquipDragOver($event, 'head')"
          @dragleave="onDragLeave"
          @drop="onEquipDrop('head')"
          @contextmenu="onEquipRightClick('head')"
        />
        <div
          class="equipment-spacer"
          :style="{ width: slotSize + 'px', height: slotSize + 'px' }"
        />
      </div>

      <!-- Row 2: Off-hand, Body, Main-hand -->
      <div class="equipment-row">
        <EquipmentSlotComponent
          slot-name="offHand"
          label="Off"
          :slot-info="getEquipmentSlotInfo('offHand')"
          :slot-size="slotSize"
          :is-valid-target="isValidDropTarget('offHand')"
          :is-dragging="isDragging"
          :drag-over-slot="dragOverEquipSlot"
          :readonly="readonly"
          @dragstart="onEquipDragStart($event, 'offHand')"
          @dragend="onDragEnd"
          @dragover="onEquipDragOver($event, 'offHand')"
          @dragleave="onDragLeave"
          @drop="onEquipDrop('offHand')"
          @contextmenu="onEquipRightClick('offHand')"
        />
        <EquipmentSlotComponent
          slot-name="body"
          label="Body"
          :slot-info="getEquipmentSlotInfo('body')"
          :slot-size="slotSize"
          :is-valid-target="isValidDropTarget('body')"
          :is-dragging="isDragging"
          :drag-over-slot="dragOverEquipSlot"
          :readonly="readonly"
          @dragstart="onEquipDragStart($event, 'body')"
          @dragend="onDragEnd"
          @dragover="onEquipDragOver($event, 'body')"
          @dragleave="onDragLeave"
          @drop="onEquipDrop('body')"
          @contextmenu="onEquipRightClick('body')"
        />
        <EquipmentSlotComponent
          slot-name="mainHand"
          label="Main"
          :slot-info="getEquipmentSlotInfo('mainHand')"
          :slot-size="slotSize"
          :is-valid-target="isValidDropTarget('mainHand')"
          :is-dragging="isDragging"
          :drag-over-slot="dragOverEquipSlot"
          :readonly="readonly"
          @dragstart="onEquipDragStart($event, 'mainHand')"
          @dragend="onDragEnd"
          @dragover="onEquipDragOver($event, 'mainHand')"
          @dragleave="onDragLeave"
          @drop="onEquipDrop('mainHand')"
          @contextmenu="onEquipRightClick('mainHand')"
        />
      </div>

      <!-- Row 3: Hands -->
      <div class="equipment-row">
        <div
          class="equipment-spacer"
          :style="{ width: slotSize + 'px', height: slotSize + 'px' }"
        />
        <EquipmentSlotComponent
          slot-name="hands"
          label="Hands"
          :slot-info="getEquipmentSlotInfo('hands')"
          :slot-size="slotSize"
          :is-valid-target="isValidDropTarget('hands')"
          :is-dragging="isDragging"
          :drag-over-slot="dragOverEquipSlot"
          :readonly="readonly"
          @dragstart="onEquipDragStart($event, 'hands')"
          @dragend="onDragEnd"
          @dragover="onEquipDragOver($event, 'hands')"
          @dragleave="onDragLeave"
          @drop="onEquipDrop('hands')"
          @contextmenu="onEquipRightClick('hands')"
        />
        <div
          class="equipment-spacer"
          :style="{ width: slotSize + 'px', height: slotSize + 'px' }"
        />
      </div>

      <!-- Row 4: Legs -->
      <div class="equipment-row">
        <div
          class="equipment-spacer"
          :style="{ width: slotSize + 'px', height: slotSize + 'px' }"
        />
        <EquipmentSlotComponent
          slot-name="legs"
          label="Legs"
          :slot-info="getEquipmentSlotInfo('legs')"
          :slot-size="slotSize"
          :is-valid-target="isValidDropTarget('legs')"
          :is-dragging="isDragging"
          :drag-over-slot="dragOverEquipSlot"
          :readonly="readonly"
          @dragstart="onEquipDragStart($event, 'legs')"
          @dragend="onDragEnd"
          @dragover="onEquipDragOver($event, 'legs')"
          @dragleave="onDragLeave"
          @drop="onEquipDrop('legs')"
          @contextmenu="onEquipRightClick('legs')"
        />
        <div
          class="equipment-spacer"
          :style="{ width: slotSize + 'px', height: slotSize + 'px' }"
        />
      </div>

      <!-- Row 5: Feet -->
      <div class="equipment-row">
        <div
          class="equipment-spacer"
          :style="{ width: slotSize + 'px', height: slotSize + 'px' }"
        />
        <EquipmentSlotComponent
          slot-name="feet"
          label="Feet"
          :slot-info="getEquipmentSlotInfo('feet')"
          :slot-size="slotSize"
          :is-valid-target="isValidDropTarget('feet')"
          :is-dragging="isDragging"
          :drag-over-slot="dragOverEquipSlot"
          :readonly="readonly"
          @dragstart="onEquipDragStart($event, 'feet')"
          @dragend="onDragEnd"
          @dragover="onEquipDragOver($event, 'feet')"
          @dragleave="onDragLeave"
          @drop="onEquipDrop('feet')"
          @contextmenu="onEquipRightClick('feet')"
        />
        <div
          class="equipment-spacer"
          :style="{ width: slotSize + 'px', height: slotSize + 'px' }"
        />
      </div>

      <!-- Row 6: Accessories (Rings and Amulet) -->
      <div class="equipment-row accessories">
        <EquipmentSlotComponent
          slot-name="ring1"
          label="Ring"
          :slot-info="getEquipmentSlotInfo('ring1')"
          :slot-size="slotSize"
          :is-valid-target="isValidDropTarget('ring1')"
          :is-dragging="isDragging"
          :drag-over-slot="dragOverEquipSlot"
          :readonly="readonly"
          @dragstart="onEquipDragStart($event, 'ring1')"
          @dragend="onDragEnd"
          @dragover="onEquipDragOver($event, 'ring1')"
          @dragleave="onDragLeave"
          @drop="onEquipDrop('ring1')"
          @contextmenu="onEquipRightClick('ring1')"
        />
        <EquipmentSlotComponent
          slot-name="amulet"
          label="Amulet"
          :slot-info="getEquipmentSlotInfo('amulet')"
          :slot-size="slotSize"
          :is-valid-target="isValidDropTarget('amulet')"
          :is-dragging="isDragging"
          :drag-over-slot="dragOverEquipSlot"
          :readonly="readonly"
          @dragstart="onEquipDragStart($event, 'amulet')"
          @dragend="onDragEnd"
          @dragover="onEquipDragOver($event, 'amulet')"
          @dragleave="onDragLeave"
          @drop="onEquipDrop('amulet')"
          @contextmenu="onEquipRightClick('amulet')"
        />
        <EquipmentSlotComponent
          slot-name="ring2"
          label="Ring"
          :slot-info="getEquipmentSlotInfo('ring2')"
          :slot-size="slotSize"
          :is-valid-target="isValidDropTarget('ring2')"
          :is-dragging="isDragging"
          :drag-over-slot="dragOverEquipSlot"
          :readonly="readonly"
          @dragstart="onEquipDragStart($event, 'ring2')"
          @dragend="onDragEnd"
          @dragover="onEquipDragOver($event, 'ring2')"
          @dragleave="onDragLeave"
          @drop="onEquipDrop('ring2')"
          @contextmenu="onEquipRightClick('ring2')"
        />
      </div>
    </div>

    <div class="section-divider" />

    <!-- Inventory Grid -->
    <div class="inventory-section-title">
      Backpack
    </div>
    <div class="inventory-grid">
      <div
        v-for="(slot, index) in inventorySlots"
        :key="index"
        class="inventory-slot"
        :class="{
          empty: !slot,
          'drag-over-empty': dragOverInventoryIndex === index && !inventorySlots[index],
          'drag-over-swap': dragOverInventoryIndex === index && inventorySlots[index],
          'drag-invalid': isDragging && !canDropToInventory,
        }"
        :style="{ width: slotSize + 'px', height: slotSize + 'px' }"
        :draggable="!readonly && !!slot"
        @dragstart="onInventoryDragStart($event, index)"
        @dragend="onDragEnd"
        @dragover.prevent="onInventoryDragOver(index)"
        @dragleave="onDragLeave"
        @drop="onInventoryDrop(index)"
        @mouseenter="hoveredSlotIndex = index"
        @mouseleave="hoveredSlotIndex = null"
        @contextmenu.prevent="onRightClick(index)"
      >
        <canvas
          v-if="slot"
          ref="slotCanvasRefs"
          :width="slotSize"
          :height="slotSize"
          :data-index="index"
          class="slot-canvas"
        />
        <div
          v-if="slot && slot.quantity > 1"
          class="slot-quantity"
        >
          {{ slot.quantity }}
        </div>
      </div>
    </div>

    <!-- Tooltip showing item name -->
    <div
      v-if="hoveredItemName"
      class="inventory-tooltip"
    >
      {{ hoveredItemName }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { InventoryIntent } from '@common/api';
import { EquipSlot, HAND_SLOTS } from '@common/data/BodyTypeSlots';
import { Texture } from 'pixi.js';
import {
  computed,
  defineComponent,
  h,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  shallowRef,
  toRaw,
  watch,
} from 'vue';

import { AssetManager } from '../AssetManager';
import { ClientWorld } from '../ClientWorld';
import { ClientDefaults } from '../components';
import { ClientServiceContainer } from '../services/ClientServiceContainer';
import {
  EquipmentSlotInfo,
  InventorySlotInfo,
  InventoryUIService,
} from '../services/InventoryUIService';
import { AnimationSystem } from '../systems/AnimationSystem';
import { AnimationTransformSystem } from '../systems/AnimationTransformSystem';
import EntityRepresentation from './EntityRepresentation.vue';

const props = defineProps<{
  entityId: number;
  inventoryService: InventoryUIService;
  assetManager: AssetManager;
  services: ClientServiceContainer;
  title?: string;
  showClose?: boolean;
  readonly?: boolean;
  intent?: InventoryIntent;
}>();

defineEmits(['close']);

// ============ Reactive State ============
const inventorySlots = ref<(InventorySlotInfo | null)[]>([]);
const equipmentSlots = ref<EquipmentSlotInfo[]>([]);
const slotCanvasRefs = ref<HTMLCanvasElement[]>([]);
const hoveredSlotIndex = ref<number | null>(null);

// Local UI state (highlights)
const dragOverInventoryIndex = ref<number | null>(null);
const dragOverEquipSlot = ref<EquipSlot | null>(null);

// Use consistent sizing based on zoom level (16px base * DEFAULT_ZOOM)
const slotSize = 16 * 4; // Hardcoded zoom level of 4

// ============ Computed Properties ============
const isDragging = computed(() => props.inventoryService.state.dragSource !== null);

const mockWorld = shallowRef<ClientWorld | null>(null);
const mockEntityId = ref<number | null>(null);
const mockEquipmentItemIds = ref<Partial<Record<EquipSlot, number>>>({});
let animSystem: AnimationSystem | null = null;
let animTransformSystem: AnimationTransformSystem | null = null;
const isLootingTarget = computed(() => {
  if (props.entityId === null || props.entityId === undefined) {
    return false;
  }
  try {
    const world = props.inventoryService.getWorld();
    const playerEntityId = props.inventoryService.getPlayerEntityId();
    
    // Explicitly exclude the player or any case where entity matches player
    if (props.entityId === playerEntityId) {
      return false;
    }

    // Looting targets are dead entities that aren't the player
    return world.hasComponent(props.entityId, 'Dead');
  } catch {
    return false;
  }
});

const portraitConfig = computed(() => {
  if (isLootingTarget.value) {
    return {
      world: props.inventoryService.getWorld(),
      entityId: props.entityId,
      isLive: true,
    };
  }

  if (mockWorld.value && mockEntityId.value !== null) {
    return {
      world: toRaw(mockWorld.value),
      entityId: mockEntityId.value,
      isLive: false,
    };
  }

  return null;
});

const entityDisplayName = computed(() => {
  const world = props.inventoryService.getWorld();
  const displayName = world.getComponent(props.entityId, 'DisplayName');
  return displayName ? displayName.name : 'Unknown';
});

function initMockWorld() {
  const world = new ClientWorld(ClientDefaults);
  mockWorld.value = world;

  // Initialize systems for mock world
  animSystem = new AnimationSystem(props.services);
  animTransformSystem = new AnimationTransformSystem(props.services);

  // Sync initial state
  syncMockEntity();
}

function syncMockEntity() {
  if (!mockWorld.value || isLootingTarget.value) return;

  const realWorld = props.inventoryService.getWorld();
  const realEntityId = props.entityId;

  // Create or update mock entity
  if (mockEntityId.value === null) {
    mockEntityId.value = mockWorld.value.createEntity();
  }

  const mockId = mockEntityId.value;

  // Sync SpritesheetSprite
  const sprite = realWorld.getComponent(realEntityId, 'SpritesheetSprite');
  if (sprite) {
    // Clone component and ensure it has an animation component to force playback
    const mockSprite = mockWorld.value.getComponent(mockId, 'SpritesheetSprite');
    if (!mockSprite) {
      mockWorld.value.addComponent(
        mockId,
        mockWorld.value.createComponent('SpritesheetSprite', { ...toRaw(sprite) }),
      );
    } else {
      mockWorld.value.updateComponent(mockId, mockSprite, { ...toRaw(sprite) });
    }

    // Force idle_down animation
    let animation = mockWorld.value.getComponent(mockId, 'Animation');
    if (!animation) {
      animation = mockWorld.value.createComponent('Animation', {
        name: 'idle_down',
        isPlaying: true,
        loop: true,
        animationFrame: 1,
        timer: 0,
      });
      mockWorld.value.addComponent(mockId, animation);
    } else if (animation.name !== 'idle_down') {
      mockWorld.value.updateComponent(mockId, animation, {
        name: 'idle_down',
        isPlaying: true,
        animationFrame: 1,
        timer: 0,
      });
    }
  }

  // Sync Equipment
  const equipment = realWorld.getComponent(realEntityId, 'Equipment');
  if (equipment) {
    const mockEquipSlots: Partial<Record<EquipSlot, number | null>> = {};

    for (const slot of HAND_SLOTS) {
      const realItemId = equipment.slots[slot];
      if (realItemId !== null && realItemId !== undefined) {
        // Get or create mock item entity
        let mockItemId = mockEquipmentItemIds.value[slot];
        if (!mockItemId) {
          mockItemId = mockWorld.value.createEntity();
          mockEquipmentItemIds.value[slot] = mockItemId;
        }

        mockEquipSlots[slot] = mockItemId;

        // Sync item SpritesheetSprite
        const itemSprite = realWorld.getComponent(realItemId, 'SpritesheetSprite');
        if (itemSprite) {
          const mockItemSprite = mockWorld.value.getComponent(mockItemId, 'SpritesheetSprite');
          if (!mockItemSprite) {
            mockWorld.value.addComponent(
              mockItemId,
              mockWorld.value.createComponent('SpritesheetSprite', { ...toRaw(itemSprite) }),
            );
          } else {
            mockWorld.value.updateComponent(mockItemId, mockItemSprite, { ...toRaw(itemSprite) });
          }
        }
      } else {
        mockEquipSlots[slot] = null;
      }
    }

    const mockEquip = mockWorld.value.getComponent(mockId, 'Equipment');
    if (!mockEquip) {
      mockWorld.value.addComponent(
        mockId,
        mockWorld.value.createComponent('Equipment', { slots: mockEquipSlots }),
      );
    } else {
      mockWorld.value.updateComponent(mockId, mockEquip, { slots: mockEquipSlots });
    }
  }

  // Sync Sprite (for visibility/tint)
  const spriteData = realWorld.getComponent(realEntityId, 'Sprite');
  if (spriteData) {
    const mockSpriteData = mockWorld.value.getComponent(mockId, 'Sprite');
    if (!mockSpriteData) {
      mockWorld.value.addComponent(
        mockId,
        mockWorld.value.createComponent('Sprite', { ...toRaw(spriteData) }),
      );
    } else {
      mockWorld.value.updateComponent(mockId, mockSpriteData, { ...toRaw(spriteData) });
    }
  }

  // Sync FaceDecoration
  const faceDeco = realWorld.getComponent(realEntityId, 'FaceDecoration');
  if (faceDeco) {
    const mockFaceDeco = mockWorld.value.getComponent(mockId, 'FaceDecoration');
    if (!mockFaceDeco) {
      mockWorld.value.addComponent(
        mockId,
        mockWorld.value.createComponent('FaceDecoration', { ...toRaw(faceDeco) }),
      );
    } else {
      mockWorld.value.updateComponent(mockId, mockFaceDeco, { ...toRaw(faceDeco) });
    }
  }

  // Sync SkinColour
  const skinColour = realWorld.getComponent(realEntityId, 'SkinColour');
  if (skinColour) {
    const mockSkinColour = mockWorld.value.getComponent(mockId, 'SkinColour');
    if (!mockSkinColour) {
      mockWorld.value.addComponent(
        mockId,
        mockWorld.value.createComponent('SkinColour', { ...toRaw(skinColour) }),
      );
    } else {
      mockWorld.value.updateComponent(mockId, mockSkinColour, { ...toRaw(skinColour) });
    }
  }
}

function tickMockWorld(deltaTime: number) {
  if (!mockWorld.value || mockEntityId.value === null) return;
  animSystem?.updateEntity({
    world: toRaw(mockWorld.value)!,
    entityId: mockEntityId.value!,
    deltaTime,
  });
  animTransformSystem?.updateEntity({
    world: toRaw(mockWorld.value)!,
    entityId: mockEntityId.value!,
    deltaTime,
  });
}

function handlePortraitTick(deltaTime: number) {
  if (portraitConfig.value && !portraitConfig.value.isLive) {
    tickMockWorld(deltaTime);
  }
}

const hoveredItemName = computed(() => {
  if (hoveredSlotIndex.value === null) return null;
  const slot = inventorySlots.value[hoveredSlotIndex.value];
  return slot ? slot.displayName : null;
});

const canDropToInventory = computed(() => {
  // Dragging from anywhere to inventory is valid
  return isDragging.value;
});

// ============ Data Fetching ============
function refreshSlots() {
  const fetched = props.inventoryService.getSlots(props.entityId);
  const max = props.inventoryService.getMaxSlots(props.entityId);

  if (fetched.length < max) {
    const padded = [...fetched];
    while (padded.length < max) {
      padded.push(null);
    }
    inventorySlots.value = padded;
  } else {
    inventorySlots.value = fetched;
  }

  equipmentSlots.value = props.inventoryService.getEquipmentSlots(props.entityId);
}

// Optimization: Create a map for O(1) lookup
const equipmentSlotInfoMap = computed(() => {
  const map = new Map<EquipSlot, EquipmentSlotInfo>();
  for (const info of equipmentSlots.value) {
    if (info) {
      map.set(info.slot, info);
    }
  }
  return map;
});

function getEquipmentSlotInfo(slot: EquipSlot): EquipmentSlotInfo | null {
  return equipmentSlotInfoMap.value.get(slot) ?? null;
}

// ============ Validity Checking for Drop Targets ============
function isValidDropTarget(slot: EquipSlot): boolean {
  const source = props.inventoryService.state.dragSource;
  if (!source) return false;

  // We need the item entity ID to check equippability.
  // In our dragstart, we can't easily find it for other panels,
  // but if it's from THIS panel, we can find it.
  let itemId: number | null = null;
  if (source.entityId === props.entityId) {
    if (source.type === 'inventory') {
      itemId = inventorySlots.value[source.slot as number]?.entityId ?? null;
    } else {
      itemId = getEquipmentSlotInfo(source.slot as EquipSlot)?.itemEntityId ?? null;
    }
  }

  // PESSIMISTIC: If it's a cross-entity drag, we don't show "valid" highlight
  // until we enhance dragSource state with item info.
  if (itemId === null) return false;

  // Check if this item can be equipped to this slot
  if (!props.inventoryService.canEquipToSlot(props.entityId, itemId, slot)) {
    return false;
  }

  // If swapping (Equipment -> Equipment), we must also check if the *target* item exists
  // and if it can move to the *source* slot.
  if (source.type === 'equipment' && source.entityId === props.entityId) {
    const sourceSlot = source.slot as EquipSlot;
    const targetItemInfo = getEquipmentSlotInfo(slot);

    // If target slot is occupied, check if its item can move to source slot
    if (targetItemInfo?.itemEntityId) {
      return props.inventoryService.canEquipToSlot(props.entityId, targetItemInfo.itemEntityId, sourceSlot);
    }
  }

  return true;
}

// ============ Inventory Drag-and-Drop ============
function onInventoryDragStart(event: DragEvent, index: number) {
  if (props.readonly) return;
  props.inventoryService.setDragSource({
    type: 'inventory',
    entityId: props.entityId,
    slot: index,
  });
  event.dataTransfer?.setData('text/plain', `inventory:${props.entityId}:${index}`);
}

function onInventoryDragOver(index: number) {
  // Prevent misleading drag-over highlights when panel is read-only
  if (props.readonly) return;
  const source = props.inventoryService.state.dragSource;
  if (source !== null) {
    // Only show highlight if it's a valid drop target
    if (
      source.type === 'inventory' &&
      source.entityId === props.entityId &&
      source.slot === index
    ) {
      return;
    }
    dragOverInventoryIndex.value = index;
  }
}

function onInventoryDrop(toIndex: number) {
  if (props.readonly) return;
  const source = props.inventoryService.state.dragSource;
  if (!source) {
    onDragEnd();
    return;
  }

  if (source.type === 'inventory') {
    // Inventory to inventory move (supports cross-entity)
    if (source.entityId !== props.entityId || source.slot !== toIndex) {
      props.inventoryService.performMove(
        source.entityId,
        source.slot as number,
        props.entityId,
        toIndex,
        props.intent,
      );
    }
  } else if (source.type === 'equipment') {
    // Equipment to inventory unequip (now supports cross-entity)
    props.inventoryService.performUnequipToInventory(
      source.entityId,
      source.slot as EquipSlot,
      props.entityId,
      toIndex,
      props.intent,
    );
  }

  refreshSlots();
  drawSlots();
  onDragEnd();
}

// ============ Equipment Drag-and-Drop ============
function onEquipDragStart(event: DragEvent, slot: EquipSlot) {
  if (props.readonly) return;
  const slotInfo = getEquipmentSlotInfo(slot);
  if (!slotInfo?.itemEntityId) return;

  props.inventoryService.setDragSource({
    type: 'equipment',
    entityId: props.entityId,
    slot,
  });
  event.dataTransfer?.setData('text/plain', `equipment:${props.entityId}:${slot}`);
}

function onEquipDragOver(event: DragEvent, slot: EquipSlot) {
  event.preventDefault();
  // Prevent misleading drag-over highlights when panel is read-only
  if (props.readonly) return;
  const source = props.inventoryService.state.dragSource;
  if (!source) return;

  // Only set dragOver if it's a valid target
  if (isValidDropTarget(slot)) {
    dragOverEquipSlot.value = slot;
  }
}

function onEquipDrop(slot: EquipSlot) {
  if (props.readonly) return;
  const source = props.inventoryService.state.dragSource;
  if (!source) {
    onDragEnd();
    return;
  }

  if (source.type === 'inventory') {
    // Inventory to equipment equip (now supports cross-entity)
    props.inventoryService.performEquipFromInventory(
      source.entityId,
      source.slot as number,
      props.entityId,
      slot,
      props.intent,
    );
  } else if (source.type === 'equipment') {
    // Equipment to equipment swap
    if (source.slot !== slot || source.entityId !== props.entityId) {
      if (source.entityId === props.entityId) {
        // Same entity swap
        props.inventoryService.performSwapEquipment(
          props.entityId,
          source.slot as EquipSlot,
          slot,
          props.intent,
        );
      } else {
        // Cross-entity swap
        props.inventoryService.performCrossEntitySwapEquipment(
          source.entityId,
          source.slot as EquipSlot,
          props.entityId,
          slot,
          props.intent,
        );
      }
    }
  }

  refreshSlots();
  drawSlots();
  onDragEnd();
}

function onEquipRightClick(slot: EquipSlot) {
  if (props.readonly) return;
  const slotInfo = getEquipmentSlotInfo(slot);
  if (slotInfo?.itemEntityId) {
    // Right-click unequips to the same entity's inventory
    props.inventoryService.performUnequipToInventory(
      props.entityId,
      slot,
      props.entityId,
      undefined,
      props.intent,
    );
    refreshSlots();
    drawSlots();
  }
}

// ============ Common Drag Handlers ============
function onDragEnd() {
  props.inventoryService.setDragSource(null);
  dragOverInventoryIndex.value = null;
  dragOverEquipSlot.value = null;
}

function onDragLeave() {
  dragOverInventoryIndex.value = null;
  dragOverEquipSlot.value = null;
}

// ============ Existing Inventory Operations ============
function onRightClick(index: number) {
  if (props.readonly) return;
  const slot = inventorySlots.value[index];
  if (slot) {
    props.inventoryService.performDrop(props.entityId, index);
    refreshSlots();
    drawSlots();
  }
}

// ============ Canvas Drawing ============
async function drawSlots() {
  await nextTick();

  slotCanvasRefs.value.forEach((canvas) => {
    const indexStr = canvas.dataset.index;
    if (!indexStr) return;

    const index = parseInt(indexStr, 10);
    const slot = inventorySlots.value[index];

    if (!slot || !slot.textureId) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.imageSmoothingEnabled = false;

    const texture = props.assetManager.getTexture(slot.textureId);
    if (!texture || texture === Texture.EMPTY) return;

    const source = texture.source;
    const resource = source.resource;

    if (
      resource instanceof HTMLImageElement ||
      resource instanceof ImageBitmap ||
      resource instanceof HTMLCanvasElement
    ) {
      const frame = texture.frame;
      ctx.drawImage(
        resource,
        frame.x,
        frame.y,
        frame.width,
        frame.height,
        0,
        0,
        canvas.width,
        canvas.height,
      );
    }
  });
}

// ============ Watchers and Lifecycle ============
watch(
  () => props.entityId,
  (newId) => {
    if (newId !== undefined) {
      refreshSlots();
      drawSlots();
    }
  },
);

watch(
  () => props.inventoryService.state.isOpen,
  (isOpen) => {
    if (isOpen) {
      refreshSlots();
      drawSlots();
    } else {
      hoveredSlotIndex.value = null;
    }
  },
);

// Watch for service-level refresh requests (e.g., from server or optimistic moves)
watch(
  () => props.inventoryService.state.refreshTick,
  () => {
    if (props.inventoryService.state.isOpen) {
      refreshSlots();
      drawSlots();
      syncMockEntity();
    }
  },
);

onMounted(() => {
  initMockWorld();
  if (props.inventoryService.state.isOpen) {
    refreshSlots();
    drawSlots();
  }
});

// Watch for target entity change
watch(
  () => props.entityId,
  () => {
    // Reset mock entity ID so it gets recreated for the new entity
    mockEntityId.value = null;
    mockEquipmentItemIds.value = {};
    if (mockWorld.value) {
      mockWorld.value.resetWorld();
    }
    syncMockEntity();
  },
);

onUnmounted(() => {
  // No cleanup needed for the watch as it's tied to the component lifecycle
});

// ============ Equipment Slot Sub-component ============
const EquipmentSlotComponent = defineComponent({
  name: 'EquipmentSlot',
  props: {
    slotName: { type: String, required: true },
    label: { type: String, required: true },
    slotInfo: { type: Object as () => EquipmentSlotInfo | null, default: null },
    slotSize: { type: Number, required: true },
    isValidTarget: { type: Boolean, default: false },
    isDragging: { type: Boolean, default: false },
    dragOverSlot: { type: String as () => EquipSlot | null, default: null },
    readonly: { type: Boolean, default: false },
  },
  emits: ['dragstart', 'dragend', 'dragover', 'dragleave', 'drop', 'contextmenu'],
  setup(slotProps, { emit }) {
    const canvasRef = ref<HTMLCanvasElement | null>(null);

    // Watch for slot info changes to redraw the item on the canvas.
    // This allows us to use standard 2D canvas drawing with pixelated
    // scaling (imageSmoothingEnabled = false) for crisp pixel art.
    watch(
      () => slotProps.slotInfo,
      async (info) => {
        await nextTick();
        const canvas = canvasRef.value;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (!info?.textureId) return;

        // Ensure crisp rendering for pixel art
        ctx.imageSmoothingEnabled = false;
        const texture = props.assetManager.getTexture(info.textureId);
        if (!texture || texture === Texture.EMPTY) return;

        const source = texture.source;
        const resource = source.resource;

        if (
          resource instanceof HTMLImageElement ||
          resource instanceof ImageBitmap ||
          resource instanceof HTMLCanvasElement
        ) {
          const frame = texture.frame;
          ctx.drawImage(
            resource,
            frame.x,
            frame.y,
            frame.width,
            frame.height,
            0,
            0,
            canvas.width,
            canvas.height,
          );
        }
      },
      { immediate: true },
    );

    return () => {
      const hasItem = !!slotProps.slotInfo?.itemEntityId;
      const classes = ['equipment-slot'];
      if (!hasItem) classes.push('empty');
      if (slotProps.isDragging && !slotProps.isValidTarget && !hasItem) {
        classes.push('drag-invalid');
      }
      if (slotProps.dragOverSlot === slotProps.slotName) {
        classes.push(slotProps.isValidTarget ? 'drag-over-valid' : 'drag-over-invalid');
      }

      return h(
        'div',
        {
          class: classes.join(' '),
          style: {
            width: `${slotProps.slotSize}px`,
            height: `${slotProps.slotSize}px`,
          },
          draggable: !slotProps.readonly && hasItem,
          onDragstart: (e: DragEvent) => emit('dragstart', e),
          onDragend: () => emit('dragend'),
          onDragover: (e: DragEvent) => emit('dragover', e),
          onDragleave: () => emit('dragleave'),
          onDrop: () => emit('drop'),
          onContextmenu: (e: MouseEvent) => {
            e.preventDefault();
            emit('contextmenu');
          },
        },
        [
          h('div', { class: 'slot-label' }, slotProps.label),
          hasItem
            ? h('canvas', {
                ref: canvasRef,
                width: slotProps.slotSize,
                height: slotProps.slotSize,
                class: 'slot-canvas',
              })
            : null,
        ],
      );
    };
  },
});
</script>

<style scoped>
.inventory-box {
  background: linear-gradient(180deg, rgba(20, 25, 35, 0.95) 0%, rgba(10, 12, 18, 0.98) 100%);
  border: 2px solid rgba(100, 140, 180, 0.4);
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
  min-width: 320px;
  position: relative;
}

.inventory-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.inventory-title {
  color: #c8d4e2;
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 600;
  font-size: 18px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.inventory-close {
  background: none;
  border: none;
  color: rgba(180, 190, 200, 0.8);
  font-size: 24px;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
}

.inventory-close:hover {
  color: #fff;
}

/* Equipment Panel */
.equipment-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-bottom: 12px;
}

.equipment-row {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.equipment-row.accessories {
  margin-top: 8px;
}

.equipment-slot {
  background: rgba(40, 50, 70, 0.5);
  border: 2px solid rgba(100, 140, 180, 0.3);
  border-radius: 4px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  transition: all 0.2s ease;
  cursor: pointer;
}

.equipment-slot.empty {
  border-style: dashed;
}

.equipment-slot:hover {
  background: rgba(60, 80, 110, 0.6);
  border-color: rgba(120, 170, 220, 0.6);
}

/* Disable hover effects in readonly mode */
.readonly .equipment-slot {
  cursor: default;
}

.readonly .equipment-slot:hover {
  background: rgba(40, 50, 70, 0.5);
  border-color: rgba(100, 140, 180, 0.3);
}

.equipment-slot.drag-over-valid {
  background: rgba(60, 180, 110, 0.3);
  border-color: #4ade80;
  transform: scale(1.05);
}

.equipment-slot.drag-over-invalid {
  background: rgba(180, 60, 60, 0.3);
  border-color: #f87171;
}

.equipment-slot.drag-invalid:not(:hover) {
  opacity: 0.5;
}

.slot-label {
  position: absolute;
  top: 2px;
  font-size: 9px;
  text-transform: uppercase;
  color: rgba(150, 170, 190, 0.8);
  pointer-events: none;
  font-weight: 500;
}

/* Inventory Grid */
.inventory-section-title {
  color: rgba(180, 190, 210, 0.6);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 8px;
  text-align: center;
}

.inventory-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
  justify-content: center;
}

.inventory-slot {
  background: rgba(40, 50, 70, 0.5);
  border: 2px solid rgba(100, 140, 180, 0.3);
  border-radius: 4px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  cursor: pointer;
}

.inventory-slot.empty {
  border-color: rgba(60, 80, 100, 0.3);
}

.inventory-slot:hover:not(.empty) {
  background: rgba(60, 80, 110, 0.6);
  border-color: rgba(120, 170, 220, 0.6);
}

/* Disable hover effects in readonly mode */
.readonly .inventory-slot {
  cursor: default;
}

.readonly .inventory-slot:hover:not(.empty) {
  background: rgba(40, 50, 70, 0.5);
  border-color: rgba(100, 140, 180, 0.3);
}

.inventory-slot.drag-over-empty {
  background: rgba(60, 180, 110, 0.3);
  border-color: #4ade80;
  transform: scale(1.05);
}

.inventory-slot.drag-over-swap {
  background: rgba(100, 140, 240, 0.3);
  border-color: #60a5fa;
  transform: scale(1.05);
}

.inventory-slot.drag-invalid:not(:hover) {
  opacity: 0.5;
}

.slot-canvas {
  width: 100%;
  height: 100%;
  pointer-events: none;
  image-rendering: pixelated;
}

.slot-quantity {
  position: absolute;
  bottom: 2px;
  right: 2px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 10px;
  padding: 0 4px;
  border-radius: 4px;
  font-family: 'Inter', system-ui, sans-serif;
  pointer-events: none;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.section-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent 0%, rgba(100, 140, 180, 0.3) 50%, transparent 100%);
  margin: 16px 0;
}

.inventory-tooltip {
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(10, 15, 25, 0.95);
  border: 1px solid rgba(100, 140, 180, 0.5);
  color: #c8d4e2;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  z-index: 1001;
}

/* Entity Preview Styles */
.entity-preview-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  background: rgba(30, 40, 60, 0.4);
  border-radius: 8px;
  padding: 12px;
  border: 1px solid rgba(100, 140, 180, 0.2);
}

.entity-name-header {
  color: #fff;
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 700;
  font-size: 16px;
  text-align: center;
  margin-bottom: 12px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  letter-spacing: 0.5px;
}

.entity-portrait-container {
  width: 120px;
  height: 120px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: radial-gradient(circle, rgba(100, 140, 180, 0.15) 0%, transparent 70%);
  border-radius: 50%;
  overflow: hidden;
}
</style>
